"use client";

import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import notificationService, {
  type AppNotification,
} from "../services/notificationService";
import { subscribeNotificationHub } from "../signalr/notificationHub";

export const NOTIFICATION_LIST_KEY = ["notifications", "list"] as const;
export const NOTIFICATION_UNREAD_KEY = ["notifications", "unread-count"] as const;

export function useGetNotifications(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATION_LIST_KEY,
    queryFn: async () => {
      const res = await notificationService.getAll();
      return (res.responseValue ?? []) as AppNotification[];
    },
    enabled,
  });
}

export function useGetUnreadNotificationCount(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATION_UNREAD_KEY,
    queryFn: async () => {
      const res = await notificationService.getUnreadCount();
      return Number(res.responseValue) || 0;
    },
    enabled,
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notifications", "read"],
    mutationFn: async (id: number) => {
      await notificationService.markAsRead(id);
      return id;
    },
    onSuccess: (id) => {
      let wasUnread = false;
      queryClient.setQueryData<AppNotification[]>(NOTIFICATION_LIST_KEY, (prev) =>
        prev?.map((item) => {
          if (item.id !== id) return item;
          wasUnread = !item.isRead;
          return { ...item, isRead: true };
        })
      );
      if (wasUnread) {
        queryClient.setQueryData<number>(NOTIFICATION_UNREAD_KEY, (count) =>
          Math.max((count ?? 1) - 1, 0)
        );
      } else {
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_KEY });
      }
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notifications", "read-all"],
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.setQueryData<AppNotification[]>(NOTIFICATION_LIST_KEY, (prev) =>
        prev?.map((item) => ({ ...item, isRead: true }))
      );
      queryClient.setQueryData<number>(NOTIFICATION_UNREAD_KEY, 0);
    },
  });
}

export function useNotificationSignalR(
  enabled: boolean,
  onReceive?: (notification: AppNotification) => void
) {
  const queryClient = useQueryClient();
  const onReceiveRef = useRef(onReceive);
  onReceiveRef.current = onReceive;

  useEffect(() => {
    if (!enabled) return;

    return subscribeNotificationHub({
      onReceiveNotification: (payload) => {
        const notification = payload as AppNotification;
        queryClient.setQueryData<AppNotification[]>(NOTIFICATION_LIST_KEY, (prev) =>
          prev
            ? [notification, ...prev.filter((item) => item.id !== notification.id)]
            : prev
        );
        queryClient.setQueryData<number>(NOTIFICATION_UNREAD_KEY, (count) =>
          (count ?? 0) + 1
        );
        onReceiveRef.current?.(notification);
      },
      onReconnected: () => {
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_KEY });
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_LIST_KEY });
      },
    });
  }, [enabled, queryClient]);
}
